import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { DBRequest, useConfig } from '../../../api/api';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import TypeSkeleton from '../../../components/form/form-fields/TypeSkeleton';
import { tokens } from '../../../global/theme/tokens';
import {
  absenceRatePercent,
  absenceSeconds,
  effectivityPercent,
  effectivityThresholdColor,
  effectivityWithAbsencePercent,
  formatPercent,
  secondsToHours,
  targetAchievementPercent,
  targetThresholdColor,
  workedDelta,
} from './workTimeReportUtils';
import WorkTimeMiniDonut from './WorkTimeMiniDonut';
import WorkTimeReportSummary from './WorkTimeReportSummary';
import WorkTimeReportProjectItem from './WorkTimeReportProjectItem';
import WorkTimeReportDonut from './WorkTimeReportDonut';

const ScoreBlock = ({ grossPct, netPct, colors, t }) => {
  const grossColor = effectivityThresholdColor(grossPct, colors);
  const netColor = effectivityThresholdColor(netPct, colors);

  return (
    <Tooltip
      title={
        <Box className="flex flex-col gap-0.5 py-0.5">
          <Typography variant="caption">
            <strong>{formatPercent(grossPct)}</strong> — {t('Customer share of worked hours')}
          </Typography>
          <Typography variant="caption">
            <strong>{formatPercent(netPct)}</strong> — {t('Customer share incl. absence')}
          </Typography>
        </Box>
      }
      arrow
      placement="left"
    >
      <Box
        className="flex flex-col items-center justify-center rounded-lg px-2 py-1.5 cursor-help"
        sx={{
          minWidth: 60,
          bgcolor: colors.card,
          border: `1px solid ${grossColor}33`,
        }}
      >
        <Typography
          sx={{
            color: grossColor,
            fontWeight: 700,
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          {formatPercent(grossPct)}
        </Typography>
        <Box
          sx={{
            width: '70%',
            height: '1px',
            bgcolor: colors.card,
            opacity: 0.4,
            my: 0.4,
          }}
        />
        <Typography
          sx={{
            color: netColor,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1,
            opacity: 0.85,
          }}
        >
          {formatPercent(netPct)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const Pill = ({ label, value, color, dim }) => (
  <Box
    className="px-2 py-1 rounded-full flex items-center gap-1"
    sx={{
      bgcolor: `${color}22`,
      border: `1px solid ${color}55`,
      opacity: dim ? 0.7 : 1,
    }}
  >
    <Typography variant="caption" sx={{ color, fontWeight: 700, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ opacity: 0.7, lineHeight: 1 }}>
      {label}
    </Typography>
  </Box>
);

const TrendChip = ({ delta, colors, t }) => {
  if (!delta) return null;
  if (delta.type === 'new') {
    return (
      <Chip
        size="small"
        icon={<FiberNewIcon sx={{ fontSize: 16 }} />}
        label={t('New')}
        sx={{
          bgcolor: `${colors.info}22`,
          color: colors.info,
          border: `1px solid ${colors.info}55`,
          fontWeight: 600,
          '.MuiChip-icon': { color: colors.info },
        }}
      />
    );
  }
  const isUp = delta.type === 'up';
  const Icon = isUp ? TrendingUpIcon : TrendingDownIcon;
  const tint = isUp ? colors.success : colors.error;
  const sign = isUp ? '+' : '';
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={`${sign}${secondsToHours(delta.deltaSeconds, 0)} h`}
      sx={{
        bgcolor: `${tint}22`,
        color: tint,
        border: `1px solid ${tint}55`,
        fontWeight: 600,
        '.MuiChip-icon': { color: tint },
      }}
    />
  );
};

const WorkTimeReportCard = ({ userOverview, previousOverview, startDate, endDate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Token used to invalidate ongoing fetches when date-range changes
  const fetchTokenRef = useRef(0);

  const userId = userOverview.user.id;

  // Invalidate cached detail when date-range changes
  useEffect(() => {
    setDetail(null);
  }, [startDate, endDate, userId]);

  // When expanded and detail not loaded yet → fetch
  useEffect(() => {
    if (!expanded || detail || !config) return;
    setLoadingDetail(true);
    const token = ++fetchTokenRef.current;
    DBRequest({
      config,
      path: `reports/work-time/${userId}?startDate=${startDate}&endDate=${endDate}`,
      onResponse: (res) => {
        if (token !== fetchTokenRef.current) return;
        setDetail(res?.data ?? null);
        setLoadingDetail(false);
      },
    });
  }, [expanded, detail, config, userId, startDate, endDate]);

  const segments = useMemo(
    () => [
      {
        key: 'customer',
        label: t('Customer hours'),
        seconds: userOverview.customerWorkedTimeInSeconds || 0,
        color: colors.success,
      },
      {
        key: 'internal',
        label: t('Internal hours'),
        seconds: userOverview.internalWorkedTimeInSeconds || 0,
        color: colors.info,
      },
      {
        key: 'payedVacation',
        label: t('Paid vacation'),
        seconds: userOverview.payedVacationInSeconds || 0,
        color: colors.warning,
      },
      {
        key: 'unpayedVacation',
        label: t('Unpaid vacation'),
        seconds: userOverview.unpayedVacationInSeconds || 0,
        color: colors.money?.negative || colors.warning,
      },
      {
        key: 'sick',
        label: t('Sick'),
        seconds: userOverview.sickTimeInSeconds || 0,
        color: colors.error,
      },
    ],
    [userOverview, colors, t],
  );

  const totalForLegend =
    (userOverview.workedTimeInSeconds || 0) + absenceSeconds(userOverview);
  const isEmpty = totalForLegend === 0;

  const effPct = effectivityPercent(userOverview);
  const targetPct = targetAchievementPercent(userOverview);
  const absencePct = absenceRatePercent(userOverview);
  const delta = workedDelta(userOverview, previousOverview);

  // Sort projects descending by user's worked time
  const sortedProjects = useMemo(() => {
    if (!detail?.projects) return [];
    return [...detail.projects].sort(
      (a, b) => (b.workedTimeInSeconds || 0) - (a.workedTimeInSeconds || 0),
    );
  }, [detail]);

  return (
    <Box
      className="rounded-lg backdrop-blur-lg p-3 flex flex-col gap-2"
      sx={{ bgcolor: colors.glass }}
    >
      <Box className="flex items-center gap-4 flex-wrap">
        {/* Identity block – left */}
        <Box className="flex items-center gap-3 min-w-[200px]">
          <ExoAvatar
            picture={userOverview.user.picture?.temporaryUrl}
            name={`${userOverview.user.firstName} ${userOverview.user.lastName}`}
            size="44px"
          />
          <Box className="flex flex-col">
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {userOverview.user.firstName} {userOverview.user.lastName}
            </Typography>
            {userOverview.user.position && (
              <Typography variant="caption" sx={{ opacity: 0.6, lineHeight: 1.2 }}>
                {userOverview.user.position}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Data block – center as one unit */}
        <Box className="flex items-center gap-3 flex-1 flex-wrap">
          <WorkTimeMiniDonut segments={segments} size={72} thickness={9} />

          <Box className="flex flex-col gap-1 flex-1 min-w-[180px]">
            <Box className="flex gap-2 flex-wrap">
              <Pill
                label={t('Target achievement')}
                value={formatPercent(targetPct)}
                color={targetThresholdColor(targetPct, colors)}
                dim={targetPct === null}
              />
              <Pill
                label={t('Absence rate')}
                value={formatPercent(absencePct)}
                color={colors.info}
                dim={absencePct === null}
              />
              <Pill
                label={t('Booking days')}
                value={userOverview.bookingDaysCount || 0}
                color={colors.text}
                dim={!userOverview.bookingDaysCount}
              />
            </Box>

            {!isEmpty && (
              <Box
                className="flex gap-x-3 gap-y-0.5 flex-wrap"
                sx={{ opacity: 0.6 }}
              >
                {segments
                  .filter((s) => s.seconds > 0)
                  .map((s) => (
                    <Box key={s.key} className="flex items-center gap-1">
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          bgcolor: s.color,
                          borderRadius: '50%',
                          filter: 'saturate(0.7)',
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {s.label} {secondsToHours(s.seconds)} h
                      </Typography>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Actions block – right */}
        <Box className="flex items-center gap-2">
          {previousOverview !== undefined && (
            <TrendChip delta={delta} colors={colors} t={t} />
          )}
          <ScoreBlock
            grossPct={effPct}
            netPct={effectivityWithAbsencePercent(userOverview)}
            colors={colors}
            t={t}
          />
          <IconButton
            onClick={() => setExpanded((p) => !p)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.25s ease',
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box className="flex flex-col gap-3 pt-2">
          <WorkTimeReportSummary userOverview={userOverview} />

          {loadingDetail || !detail ? (
            <TypeSkeleton n={3} />
          ) : (
            <>
              {detail.projects?.length >= 1 && (
                <WorkTimeReportDonut projects={detail.projects} />
              )}

              {sortedProjects.length === 0 ? (
                <Box
                  className="p-4 rounded-lg backdrop-blur-lg text-center"
                  sx={{ bgcolor: colors.card }}
                >
                  <Typography sx={{ opacity: 0.7 }} variant="body2">
                    {t('No projects with bookings')}
                  </Typography>
                </Box>
              ) : (
                <Box className="flex flex-col gap-2">
                  {sortedProjects.map((p) => (
                    <WorkTimeReportProjectItem
                      key={p.project.id}
                      projectData={p}
                      userId={userId}
                      user={userOverview.user}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default WorkTimeReportCard;
